// Pull script rows out of a ProseMirror doc for the Script Shoot View.
//
// A script row is a `scriptRow` node holding two `scriptCell`s: the audio
// cell (`kind: 'audio'`, optional `audioType`) and the visuals cell. Rows are
// collected in document order wherever they sit, and each one remembers the
// nearest heading above it so a campaign's variants can be grouped on screen.
//
// One walker serves the shoot view, its entry points and the service summary
// route, so the counts the call sheet shows always match the page it opens.

import { BRIEF_TEMPLATE_KEY } from './briefFields';
import { normalizeAudioType, type AudioType } from './audioTypes';
import { normalizeVisualType, type VisualType } from './visualTypes';
import { hasInterviewQuestions, hasShootContent as hasBriefShootContent } from './extractInterviewQuestions';

interface PMNode {
  type: string;
  attrs?: Record<string, unknown>;
  content?: PMNode[];
  text?: string;
}

export interface ShootScriptRow {
  /** 1-based position among the doc's rows ("Row 3"). */
  number: number;
  /** `null` is an untyped legacy row: it shows without a chip. */
  audioType: AudioType | null;
  /** `null` is an untyped legacy row: no chip. */
  visualType: VisualType | null;
  /** The audio cell's paragraphs, trimmed, empties dropped. */
  audio: string[];
  /** The visuals cell's paragraphs, trimmed, empties dropped. */
  visuals: string[];
  /** Text of the nearest heading above the row, or null before any heading. */
  section: string | null;
}

export interface ScriptRowCounts {
  total_rows: number;
  to_camera_rows: number;
  voiceover_rows: number;
}

function nodeText(node: PMNode | undefined): string {
  if (!node) return '';
  if (node.type === 'text') return node.text ?? '';
  if (node.type === 'hardBreak') return '\n';
  if (!node.content) return '';
  return node.content.map(nodeText).join('');
}

function cellParagraphs(cell: PMNode | undefined): string[] {
  return (cell?.content ?? []).map((p) => nodeText(p).trim()).filter(Boolean);
}

function toRow(node: PMNode, number: number, section: string | null): ShootScriptRow {
  const cells = node.content ?? [];
  const audioCell = cells.find((c) => c.attrs?.kind === 'audio') ?? cells[0];
  const visualsCell = cells.find((c) => c.attrs?.kind === 'visuals') ?? cells[1];
  return {
    number,
    audioType: normalizeAudioType(audioCell?.attrs?.audioType),
    visualType: normalizeVisualType(visualsCell?.attrs?.visualType),
    audio: cellParagraphs(audioCell),
    visuals: cellParagraphs(visualsCell),
    section,
  };
}

export function extractScriptRows(docJson: string): ShootScriptRow[] {
  let doc: PMNode;
  try {
    doc = JSON.parse(docJson);
  } catch {
    return [];
  }
  const rows: ShootScriptRow[] = [];
  let section: string | null = null;
  const walk = (nodes: PMNode[] | undefined) => {
    for (const node of nodes ?? []) {
      if (node?.type === 'heading') {
        section = nodeText(node).trim() || null;
      } else if (node?.type === 'scriptRow') {
        rows.push(toRow(node, rows.length + 1, section));
      } else if (Array.isArray(node?.content)) {
        walk(node.content);
      }
    }
  };
  walk(Array.isArray(doc?.content) ? doc.content : []);
  return rows;
}

/** Rows with neither audio nor visuals have nothing to read on set. The page,
 *  its entry points and the summary counts all skip them, so they agree. */
export function shootableRows(rows: ShootScriptRow[]): ShootScriptRow[] {
  return rows.filter((r) => r.audio.length > 0 || r.visuals.length > 0);
}

export function countScriptRows(allRows: ShootScriptRow[]): ScriptRowCounts {
  const rows = shootableRows(allRows);
  return {
    total_rows: rows.length,
    to_camera_rows: rows.filter((r) => r.audioType === 'to_camera').length,
    voiceover_rows: rows.filter((r) => r.audioType === 'voiceover').length,
  };
}

/** Briefs and scripts share the same Shoot View routes but not the same page. */
export function shootFamily(templateKey: unknown): 'brief' | 'script' {
  return templateKey === BRIEF_TEMPLATE_KEY ? 'brief' : 'script';
}

/**
 * The single predicate for every Shoot View entry point, briefs and scripts.
 * A brief needs questions or shoot-visible fields (the brief rule); a script
 * needs a non-empty row or a legacy "Interview questions" section.
 */
export function hasShootView(docJson: string, templateKey: unknown): boolean {
  if (shootFamily(templateKey) === 'brief') return hasBriefShootContent(docJson);
  // Brief fields in a script are ignored: the script page never renders them.
  return shootableRows(extractScriptRows(docJson)).length > 0 || hasInterviewQuestions(docJson);
}
